/**
 * Search Enhancements for Volume Theme
 * 1. Animated placeholder text cycling
 * 2. Voice search (SpeechRecognition browsers, graceful fallback)
 */

(function () {
  /* -- Animated Placeholder ----------------------------------------- */

  var PLACEHOLDERS = (function () {
    var script = document.querySelector('script[data-placeholders]');
    if (script) {
      var raw = script.getAttribute('data-placeholders');
      var items = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      if (items.length >= 2) return items;
    }
    return ['Search for vinyl...', 'Search for artists...', 'Search for posters...'];
  })();

  const TYPING_SPEED = 90;
  const DELETING_SPEED = 55;
  const DELAY_AFTER_WORD = 2400;
  const DELAY_AFTER_DELETE = 400;
  const DELAY_BEFORE_FIRST = 2000;

  function initAnimatedPlaceholder(input) {
    if (!input || PLACEHOLDERS.length < 2) return;

    // Prevent duplicate animation chains
    if (input._placeholderAnimationActive) return;
    input._placeholderAnimationActive = true;

    let currentIndex = 0;
    let stopped = false;
    let currentTimeout = null;
    let currentInterval = null;
    input.setAttribute('placeholder', PLACEHOLDERS[0]);

    function cleanup() {
      stopped = true;
      if (currentTimeout) clearTimeout(currentTimeout);
      if (currentInterval) clearInterval(currentInterval);
      input._placeholderAnimationActive = false;
    }

    // Stop animation when input is removed from DOM
    var disconnectObserver = new MutationObserver(function () {
      if (!document.body.contains(input)) {
        cleanup();
        disconnectObserver.disconnect();
      }
    });
    disconnectObserver.observe(document.body, { childList: true, subtree: true });

    // Also stop on searchmodal:close
    document.addEventListener('searchmodal:close', cleanup, { once: true });

    function typeText(text, fromIndex) {
      return new Promise(function (resolve) {
        if (stopped) return;
        var current = input.getAttribute('placeholder');
        var remaining = text.slice(fromIndex);
        var i = 0;
        currentInterval = setInterval(function () {
          if (stopped) { clearInterval(currentInterval); return; }
          current = input.getAttribute('placeholder');
          input.setAttribute('placeholder', current + remaining.charAt(i));
          i++;
          if (i >= remaining.length) {
            clearInterval(currentInterval);
            currentInterval = null;
            resolve();
          }
        }, TYPING_SPEED);
      });
    }

    function deleteText(keepPrefix) {
      return new Promise(function (resolve) {
        if (stopped) return;
        currentInterval = setInterval(function () {
          if (stopped) { clearInterval(currentInterval); return; }
          var current = input.getAttribute('placeholder');
          var next = current.slice(0, -1);
          input.setAttribute('placeholder', next);
          if (next.length <= keepPrefix) {
            clearInterval(currentInterval);
            currentInterval = null;
            resolve();
          }
        }, DELETING_SPEED);
      });
    }

    function findCommonPrefix(a, b) {
      var len = Math.min(a.length, b.length);
      var i = 0;
      while (i < len && a[i] === b[i]) i++;
      return i >= 3 ? i : 0;
    }

    function cycle() {
      if (stopped) return;
      var nextIndex = (currentIndex + 1) % PLACEHOLDERS.length;
      var current = PLACEHOLDERS[currentIndex];
      var next = PLACEHOLDERS[nextIndex];
      var commonLen = findCommonPrefix(current, next);

      deleteText(commonLen).then(function () {
        if (stopped) return;
        return new Promise(function (r) { currentTimeout = setTimeout(r, DELAY_AFTER_DELETE); });
      }).then(function () {
        if (stopped) return;
        return typeText(next, commonLen);
      }).then(function () {
        if (stopped) return;
        currentIndex = nextIndex;
        currentTimeout = setTimeout(cycle, DELAY_AFTER_WORD);
      });
    }

    currentTimeout = setTimeout(cycle, DELAY_BEFORE_FIRST);
  }

  /* -- Voice Search ------------------------------------------------- */

  function initVoiceSearch(input) {
    if (!input) return;

    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    var form = input.closest('form');
    if (!form) return;

    var recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    var isListening = false;

    // Create mic button
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'search-voice-btn';
    btn.setAttribute('aria-label', 'Search by voice');
    btn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm-1-9a1 1 0 1 1 2 0v6a1 1 0 1 1-2 0V5zm6 6a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V20H8v2h8v-2h-3v-2.07A7 7 0 0 0 19 11h-2z"/></svg>';

    // Insert after the input's parent field wrapper
    var field = input.closest('.field, .search-field');
    if (field) {
      field.style.position = 'relative';
      field.appendChild(btn);
    } else {
      return;
    }

    function toggleListen(e) {
      e.preventDefault();
      e.stopPropagation();
      if (isListening) {
        recognition.stop();
      } else {
        recognition.start();
      }
    }

    recognition.addEventListener('audiostart', function () {
      isListening = true;
      btn.classList.add('search-voice-btn--listening');
    });

    recognition.addEventListener('audioend', function () {
      isListening = false;
      btn.classList.remove('search-voice-btn--listening');
    });

    recognition.addEventListener('result', function (e) {
      if (e.results && e.results[0]) {
        var transcript = e.results[0][0].transcript;
        input.value = transcript;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    recognition.addEventListener('error', function () {
      isListening = false;
      btn.classList.remove('search-voice-btn--listening');
    });

    btn.addEventListener('click', toggleListen);
    btn.addEventListener('keydown', function (e) {
      if (e.code === 'Space' || e.code === 'Enter') toggleListen(e);
    });
  }

  /* -- Smooth Expand / Collapse -------------------------------------- */

  function initExpandCollapse(input) {
    if (window.innerWidth < 1024) return;

    var container = input.closest('.search.search--field');
    if (!container) return;

    var startWidth = container.offsetWidth;
    var targetWidth = window.innerWidth >= 1280 ? 380 : 320;
    var animId = null;
    var currentWidth = startWidth;
    var expanded = false;

    // Update startWidth on resize so collapse targets the correct width
    window.addEventListener('resize', function () {
      if (!expanded) {
        startWidth = container.offsetWidth;
      }
      targetWidth = window.innerWidth >= 1280 ? 380 : 320;
    });

    function animate() {
      var goal = expanded ? targetWidth : startWidth;
      var diff = goal - currentWidth;

      if (Math.abs(diff) < 1) {
        currentWidth = goal;
        container.style.width = goal + 'px';
        animId = null;
        if (!expanded) container.style.willChange = '';
        return;
      }

      // Ease-out: move 12% of remaining distance each frame
      currentWidth += diff * 0.12;
      container.style.width = currentWidth + 'px';
      animId = requestAnimationFrame(animate);
    }

    function expand() {
      if (expanded) return;
      expanded = true;
      container.style.willChange = 'width';
      if (animId) cancelAnimationFrame(animId);
      animId = requestAnimationFrame(animate);
    }

    function collapse() {
      if (!expanded) return;
      expanded = false;
      if (animId) cancelAnimationFrame(animId);
      animId = requestAnimationFrame(animate);
    }

    input.addEventListener('focus', expand);
    input.addEventListener('blur', function () {
      setTimeout(function () {
        if (!container.contains(document.activeElement)) {
          collapse();
        }
      }, 150);
    });

    // Collapse when the X close button is clicked
    var closeBtns = document.querySelectorAll('.button-close--search');
    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        collapse();
      });
    });

    // Collapse when clicking outside
    document.addEventListener('click', function (e) {
      if (expanded && !container.closest('.search--field').contains(e.target)) {
        collapse();
      }
    });
  }

  /* -- Init ---------------------------------------------------------- */

  function enhance(input) {
    if (!input || input.dataset.enhancementsInit) return;
    // Only enhance visible inputs (skip hidden modal/drawer inputs until they're shown)
    var rect = input.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    // Skip the search results page input (it's inside <main> and has the magnifier
    // on the RIGHT side, so our mic would overlap it)
    if (input.closest('main, .main-content, .template-search')) return;
    input.dataset.enhancementsInit = 'true';
    initAnimatedPlaceholder(input);
    initVoiceSearch(input);
    initExpandCollapse(input);
  }

  function initAll() {
    var inputs = document.querySelectorAll('input[type="search"][name="q"]');
    for (var i = 0; i < inputs.length; i++) {
      enhance(inputs[i]);
    }
  }

  // Run on load for any inputs already in the DOM.
  initAll();

  // Debounced MutationObserver for dynamically added/shown search inputs.
  var debounceTimer = null;
  var observer = new MutationObserver(function () {
    if (debounceTimer) return;
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      initAll();
    }, 200);
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
